package dev.helpmycity.`data`.local.database

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class DepartmentDao_Impl(
  __db: RoomDatabase,
) : DepartmentDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfDepartmentEntity: EntityUpsertAdapter<DepartmentEntity>
  init {
    this.__db = __db
    this.__upsertAdapterOfDepartmentEntity = EntityUpsertAdapter<DepartmentEntity>(object : EntityInsertAdapter<DepartmentEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `departments` (`id`,`name`,`contact_email`,`contact_phone`,`handles_categories`,`sync_state`,`last_synced_at`,`remote_version`) VALUES (?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: DepartmentEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        val _tmpContactEmail: String? = entity.contactEmail
        if (_tmpContactEmail == null) {
          statement.bindNull(3)
        } else {
          statement.bindText(3, _tmpContactEmail)
        }
        val _tmpContactPhone: String? = entity.contactPhone
        if (_tmpContactPhone == null) {
          statement.bindNull(4)
        } else {
          statement.bindText(4, _tmpContactPhone)
        }
        statement.bindText(5, entity.handlesCategories)
        statement.bindText(6, entity.syncState)
        val _tmpLastSyncedAtMillis: Long? = entity.lastSyncedAtMillis
        if (_tmpLastSyncedAtMillis == null) {
          statement.bindNull(7)
        } else {
          statement.bindLong(7, _tmpLastSyncedAtMillis)
        }
        val _tmpRemoteVersion: String? = entity.remoteVersion
        if (_tmpRemoteVersion == null) {
          statement.bindNull(8)
        } else {
          statement.bindText(8, _tmpRemoteVersion)
        }
      }
    }, object : EntityDeleteOrUpdateAdapter<DepartmentEntity>() {
      protected override fun createQuery(): String = "UPDATE `departments` SET `id` = ?,`name` = ?,`contact_email` = ?,`contact_phone` = ?,`handles_categories` = ?,`sync_state` = ?,`last_synced_at` = ?,`remote_version` = ? WHERE `id` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: DepartmentEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        val _tmpContactEmail: String? = entity.contactEmail
        if (_tmpContactEmail == null) {
          statement.bindNull(3)
        } else {
          statement.bindText(3, _tmpContactEmail)
        }
        val _tmpContactPhone: String? = entity.contactPhone
        if (_tmpContactPhone == null) {
          statement.bindNull(4)
        } else {
          statement.bindText(4, _tmpContactPhone)
        }
        statement.bindText(5, entity.handlesCategories)
        statement.bindText(6, entity.syncState)
        val _tmpLastSyncedAtMillis: Long? = entity.lastSyncedAtMillis
        if (_tmpLastSyncedAtMillis == null) {
          statement.bindNull(7)
        } else {
          statement.bindLong(7, _tmpLastSyncedAtMillis)
        }
        val _tmpRemoteVersion: String? = entity.remoteVersion
        if (_tmpRemoteVersion == null) {
          statement.bindNull(8)
        } else {
          statement.bindText(8, _tmpRemoteVersion)
        }
        statement.bindText(9, entity.id)
      }
    })
  }

  public override suspend fun upsert(department: DepartmentEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfDepartmentEntity.upsert(_connection, department)
  }

  public override suspend fun upsertAll(departments: List<DepartmentEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfDepartmentEntity.upsert(_connection, departments)
  }

  public override fun observeAll(): Flow<List<DepartmentEntity>> {
    val _sql: String = "SELECT * FROM departments ORDER BY name ASC"
    return createFlow(__db, false, arrayOf("departments")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfContactEmail: Int = getColumnIndexOrThrow(_stmt, "contact_email")
        val _columnIndexOfContactPhone: Int = getColumnIndexOrThrow(_stmt, "contact_phone")
        val _columnIndexOfHandlesCategories: Int = getColumnIndexOrThrow(_stmt, "handles_categories")
        val _columnIndexOfSyncState: Int = getColumnIndexOrThrow(_stmt, "sync_state")
        val _columnIndexOfLastSyncedAtMillis: Int = getColumnIndexOrThrow(_stmt, "last_synced_at")
        val _columnIndexOfRemoteVersion: Int = getColumnIndexOrThrow(_stmt, "remote_version")
        val _result: MutableList<DepartmentEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: DepartmentEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpContactEmail: String?
          if (_stmt.isNull(_columnIndexOfContactEmail)) {
            _tmpContactEmail = null
          } else {
            _tmpContactEmail = _stmt.getText(_columnIndexOfContactEmail)
          }
          val _tmpContactPhone: String?
          if (_stmt.isNull(_columnIndexOfContactPhone)) {
            _tmpContactPhone = null
          } else {
            _tmpContactPhone = _stmt.getText(_columnIndexOfContactPhone)
          }
          val _tmpHandlesCategories: String
          _tmpHandlesCategories = _stmt.getText(_columnIndexOfHandlesCategories)
          val _tmpSyncState: String
          _tmpSyncState = _stmt.getText(_columnIndexOfSyncState)
          val _tmpLastSyncedAtMillis: Long?
          if (_stmt.isNull(_columnIndexOfLastSyncedAtMillis)) {
            _tmpLastSyncedAtMillis = null
          } else {
            _tmpLastSyncedAtMillis = _stmt.getLong(_columnIndexOfLastSyncedAtMillis)
          }
          val _tmpRemoteVersion: String?
          if (_stmt.isNull(_columnIndexOfRemoteVersion)) {
            _tmpRemoteVersion = null
          } else {
            _tmpRemoteVersion = _stmt.getText(_columnIndexOfRemoteVersion)
          }
          _item = DepartmentEntity(_tmpId,_tmpName,_tmpContactEmail,_tmpContactPhone,_tmpHandlesCategories,_tmpSyncState,_tmpLastSyncedAtMillis,_tmpRemoteVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getById(id: String): DepartmentEntity? {
    val _sql: String = "SELECT * FROM departments WHERE id = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfContactEmail: Int = getColumnIndexOrThrow(_stmt, "contact_email")
        val _columnIndexOfContactPhone: Int = getColumnIndexOrThrow(_stmt, "contact_phone")
        val _columnIndexOfHandlesCategories: Int = getColumnIndexOrThrow(_stmt, "handles_categories")
        val _columnIndexOfSyncState: Int = getColumnIndexOrThrow(_stmt, "sync_state")
        val _columnIndexOfLastSyncedAtMillis: Int = getColumnIndexOrThrow(_stmt, "last_synced_at")
        val _columnIndexOfRemoteVersion: Int = getColumnIndexOrThrow(_stmt, "remote_version")
        val _result: DepartmentEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpContactEmail: String?
          if (_stmt.isNull(_columnIndexOfContactEmail)) {
            _tmpContactEmail = null
          } else {
            _tmpContactEmail = _stmt.getText(_columnIndexOfContactEmail)
          }
          val _tmpContactPhone: String?
          if (_stmt.isNull(_columnIndexOfContactPhone)) {
            _tmpContactPhone = null
          } else {
            _tmpContactPhone = _stmt.getText(_columnIndexOfContactPhone)
          }
          val _tmpHandlesCategories: String
          _tmpHandlesCategories = _stmt.getText(_columnIndexOfHandlesCategories)
          val _tmpSyncState: String
          _tmpSyncState = _stmt.getText(_columnIndexOfSyncState)
          val _tmpLastSyncedAtMillis: Long?
          if (_stmt.isNull(_columnIndexOfLastSyncedAtMillis)) {
            _tmpLastSyncedAtMillis = null
          } else {
            _tmpLastSyncedAtMillis = _stmt.getLong(_columnIndexOfLastSyncedAtMillis)
          }
          val _tmpRemoteVersion: String?
          if (_stmt.isNull(_columnIndexOfRemoteVersion)) {
            _tmpRemoteVersion = null
          } else {
            _tmpRemoteVersion = _stmt.getText(_columnIndexOfRemoteVersion)
          }
          _result = DepartmentEntity(_tmpId,_tmpName,_tmpContactEmail,_tmpContactPhone,_tmpHandlesCategories,_tmpSyncState,_tmpLastSyncedAtMillis,_tmpRemoteVersion)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun count(): Int {
    val _sql: String = "SELECT COUNT(*) FROM departments"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun delete(id: String) {
    val _sql: String = "DELETE FROM departments WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
